// frontend/src/pages/Ingestion.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getOrganizations } from "../api/accessProvisioning";
import { wipeReingest, ingestNew, getIngestionStatus, getIngestionErrors, retryErrors } from "../api/ingestion";

const ACTIONS = {
  wipe: {
    label: "Wipe & Re-ingest",
    description:
      "Removes all discovered documents for the Azure Blob Storage connector and schedules a full re-ingest from scratch.",
    btnClass: "btn-danger",
  },
  ingest: {
    label: "Ingest New Data",
    description:
      "Schedules a new ingest request using the metadata from the most recent previous request. Existing documents are not removed.",
    btnClass: "btn-primary",
  },
};

// Layers highlighted as primary "files to ingest"
const FILE_LAYERS = new Set(["discovered_files", "loaded_files"]);

// All known layers in display order (mirrors backend _FULL_STATUS_QUERY stages)
const KNOWN_LAYERS = [
  "discovered_files",
  "loaded_files",
  "extracted_documents",
  "detected_documents (source)",
  "detected_documents (past-performance)",
  "detected_documents (resume)",
  "searchable_documents",
  "searchable_paragraphs",
  "searchable_tables",
  "searchable_images",
];

const AUTO_REFRESH_MS = 30_000; // 30 seconds

export default function Ingestion() {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [orgsError, setOrgsError] = useState("");

  // Action selection
  const [selectedAction, setSelectedAction] = useState("ingest");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null); // { action, time, ok, message }

  // Status table — never cleared once loaded; refreshes in-place
  const [statusRows, setStatusRows] = useState(null);
  const [statusSkipped, setStatusSkipped] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [statusTime, setStatusTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Error files
  const [errorRows, setErrorRows] = useState(null);
  const [errorLoading, setErrorLoading] = useState(false);
  const [errorError, setErrorError] = useState("");

  // Retry selected errors
  const [selectedErrors, setSelectedErrors] = useState(new Set()); // keys: `${layer}::${filepath}`
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null); // { ok, message }

  const selectedOrgIdRef = useRef(selectedOrgId);
  selectedOrgIdRef.current = selectedOrgId;

  useEffect(() => {
    getOrganizations()
      .then((data) => {
        const sorted = (data || []).slice().sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(sorted);
      })
      .catch((err) => setOrgsError(err?.message || "Failed to load organizations"))
      .finally(() => setLoadingOrgs(false));
  }, []);

  const fetchStatus = useCallback(async (orgId) => {
    if (!orgId) return;
    setStatusLoading(true);
    setStatusError("");
    try {
      const data = await getIngestionStatus(Number(orgId));
      if (orgId === selectedOrgIdRef.current) {
        setStatusRows(data.rows || []);
        setStatusSkipped(data.skipped || []);
        setStatusTime(new Date());
      }
    } catch (err) {
      if (orgId === selectedOrgIdRef.current) {
        setStatusError(err?.message || "Failed to load ingestion status");
      }
    } finally {
      if (orgId === selectedOrgIdRef.current) {
        setStatusLoading(false);
      }
    }
  }, []);

  const fetchErrors = useCallback(async (orgId) => {
    if (!orgId) return;
    setErrorLoading(true);
    setErrorError("");
    try {
      const data = await getIngestionErrors(Number(orgId));
      if (orgId === selectedOrgIdRef.current) {
        setErrorRows(data.rows || []);
      }
    } catch (err) {
      if (orgId === selectedOrgIdRef.current) {
        setErrorError(err?.message || "Failed to load ingestion errors");
      }
    } finally {
      if (orgId === selectedOrgIdRef.current) {
        setErrorLoading(false);
      }
    }
  }, []);

  // Fetch once immediately when org changes; clear stale state first
  useEffect(() => {
    if (!selectedOrgId) return;
    setStatusRows(null);
    setStatusSkipped([]);
    setErrorRows(null);
    setSelectedErrors(new Set());
    setRetryResult(null);
    fetchStatus(selectedOrgId);
    fetchErrors(selectedOrgId);
  }, [selectedOrgId, fetchStatus, fetchErrors]);

  // Auto-refresh every 30s — only active while autoRefresh is on
  useEffect(() => {
    if (!autoRefresh || !selectedOrgId) return;
    const id = setInterval(() => {
      fetchStatus(selectedOrgIdRef.current);
      fetchErrors(selectedOrgIdRef.current);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, selectedOrgId, fetchStatus, fetchErrors]);

  // Clear confirm when action changes
  useEffect(() => {
    setConfirm(false);
  }, [selectedAction]);

  const selectedOrgName = orgs.find((o) => String(o.id) === selectedOrgId)?.name || "";

  async function handleExecute() {
    if (!selectedOrgId) return;
    const action = ACTIONS[selectedAction];

    if (selectedAction === "wipe" && !confirm) {
      setConfirm(true);
      return;
    }

    setBusy(true);
    setConfirm(false);
    try {
      let msg;
      if (selectedAction === "wipe") {
        const data = await wipeReingest(Number(selectedOrgId));
        msg = `Deleted ${data.deleted_rows ?? "?"} document(s). Re-ingest scheduled.`;
      } else {
        await ingestNew(Number(selectedOrgId));
        msg = "Ingest request submitted successfully.";
      }
      setLastRun({ action: action.label, time: new Date(), ok: true, message: msg });
      fetchStatus(selectedOrgId);
      fetchErrors(selectedOrgId);
    } catch (err) {
      setLastRun({
        action: action.label,
        time: new Date(),
        ok: false,
        message: err?.message || "Operation failed",
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleRetry() {
    if (!selectedOrgId || selectedErrors.size === 0) return;
    const errors = [...selectedErrors].map((key) => {
      const idx = key.indexOf("::");
      return { layer: key.slice(0, idx), filepath: key.slice(idx + 2) };
    });
    setRetrying(true);
    setRetryResult(null);
    try {
      const data = await retryErrors(Number(selectedOrgId), errors);
      setRetryResult({ ok: true, message: `${data.reset_count} file(s) re-queued.` });
      setSelectedErrors(new Set());
      fetchStatus(selectedOrgId);
      fetchErrors(selectedOrgId);
    } catch (err) {
      setRetryResult({ ok: false, message: err?.message || "Retry failed" });
    } finally {
      setRetrying(false);
    }
  }

  // Build table data
  const layerMap = {};
  for (const row of statusRows || []) {
    if (!layerMap[row.layer]) layerMap[row.layer] = {};
    layerMap[row.layer][row.status] = row.count;
  }
  const skippedSet = new Set(statusSkipped);
  // Always show all known layers; append any unexpected extras at the end
  const knownSet = new Set(KNOWN_LAYERS);
  const extraLayers = Object.keys(layerMap).filter((l) => !knownSet.has(l));
  const layers = statusRows !== null ? [...KNOWN_LAYERS, ...extraLayers] : [];
  const allStatuses = [...new Set((statusRows || []).map((r) => r.status))].sort();

  const action = ACTIONS[selectedAction];

  return (
    <div className="row g-3">
      {/* Left: controls */}
      <div className="col-12 col-lg-5 col-xl-4">
        {/* Org */}
        <div className="card shadow-sm mb-3">
          <div className="card-header py-2">Organization</div>
          <div className="card-body">
            {loadingOrgs ? (
              <div className="text-muted small">Loading…</div>
            ) : orgsError ? (
              <div className="text-danger small">{orgsError}</div>
            ) : (
              <select
                className="form-select form-select-sm"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                  setLastRun(null);
                  setConfirm(false);
                }}
              >
                <option value="">— Select an organization —</option>
                {orgs.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Action selection */}
        <div className="card shadow-sm mb-3">
          <div className="card-header py-2">Action</div>
          <div className="card-body pb-2">
            {Object.entries(ACTIONS).map(([key, meta]) => (
              <div
                key={key}
                className={`border rounded p-3 mb-2 ${selectedAction === key ? "border-primary bg-light" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedAction(key)}
              >
                <div className="d-flex align-items-start gap-2">
                  <input
                    type="radio"
                    className="form-check-input mt-1 flex-shrink-0"
                    checked={selectedAction === key}
                    readOnly
                  />
                  <div>
                    <div className="fw-semibold small">{meta.label}</div>
                    <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                      {meta.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Last run result — persists until org changes */}
            {lastRun && (
              <div
                className={`alert py-2 small mt-1 mb-2 ${lastRun.ok ? "alert-success" : "alert-danger"}`}
                role="alert"
              >
                <strong>{lastRun.action}</strong> at{" "}
                {lastRun.time.toLocaleTimeString()} — {lastRun.message}
              </div>
            )}

            {/* Confirm banner */}
            {confirm && selectedAction === "wipe" && (
              <div className="alert alert-warning py-2 small mb-2">
                Delete all discovered documents for <strong>{selectedOrgName}</strong>?
              </div>
            )}

            <div className="d-flex gap-2">
              <button
                className={`btn btn-sm ${action.btnClass}`}
                disabled={!selectedOrgId || busy}
                onClick={handleExecute}
              >
                {busy ? "Running…" : confirm ? "Confirm" : action.label}
              </button>
              {confirm && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setConfirm(false)}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right: status */}
      <div className="col-12 col-lg-7 col-xl-8">
        {/* Status table */}
        <div className="card shadow-sm">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <span>
              Ingestion Status
              {statusTime && (
                <span className="text-muted small ms-2">
                  updated {statusTime.toLocaleTimeString()}
                </span>
              )}
            </span>
            <div className="d-flex gap-2 align-items-center">
              {autoRefresh && (
                <span className="text-muted small">
                  every 30s{statusLoading && <span className="ms-1">↻</span>}
                </span>
              )}
              <button
                className={`btn btn-sm ${autoRefresh ? "btn-success" : "btn-outline-secondary"}`}
                disabled={!selectedOrgId}
                onClick={() => setAutoRefresh((v) => !v)}
              >
                {autoRefresh ? "Live: On" : "Live: Off"}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={statusLoading || !selectedOrgId}
                onClick={() => { fetchStatus(selectedOrgId); fetchErrors(selectedOrgId); }}
              >
                {statusLoading && !statusTime ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {/* First-load spinner */}
            {statusRows === null && statusLoading && (
              <div className="p-3 text-muted small">Loading status…</div>
            )}
            {/* Error before first load */}
            {statusError && statusRows === null && (
              <div className="p-3 text-danger small">{statusError}</div>
            )}
            {/* Table — always shown once loaded, all known layers always visible */}
            {statusRows !== null && (
              <>
                {statusError && (
                  <div className="px-3 pt-2 text-danger small">{statusError}</div>
                )}
                {statusSkipped.length > 0 && (
                  <div className="px-3 pt-2 text-warning small">
                    Skipped (table missing or timed out): {statusSkipped.join(", ")}
                  </div>
                )}
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Layer</th>
                        {allStatuses.map((s) => (
                          <th key={s} className="text-end">{s}</th>
                        ))}
                        <th className="text-end">total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {layers.map((layer) => {
                        const counts = layerMap[layer];
                        const isSkipped = skippedSet.has(layer) || !counts;
                        const total = isSkipped
                          ? null
                          : Object.values(counts).reduce((a, b) => a + b, 0);
                        return (
                          <tr
                            key={layer}
                            className={FILE_LAYERS.has(layer) ? "table-info" : ""}
                          >
                            <td className="font-monospace small">{layer}</td>
                            {isSkipped ? (
                              <td
                                colSpan={allStatuses.length + 1}
                                className="text-muted text-end small fst-italic"
                              >
                                —
                              </td>
                            ) : (
                              <>
                                {allStatuses.map((s) => (
                                  <td key={s} className="text-end">
                                    {counts[s] != null ? counts[s].toLocaleString() : "—"}
                                  </td>
                                ))}
                                <td className="text-end fw-semibold">
                                  {total.toLocaleString()}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

      </div>

      {/* Errored files — full width below both columns */}
      <div className="col-12">
        <div className="card shadow-sm border-danger border-opacity-25">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              Errored Files
              {errorRows !== null && (
                <span className="text-muted fw-normal small ms-2">({errorRows.length} file{errorRows.length !== 1 ? "s" : ""})</span>
              )}
            </span>
            <div className="d-flex gap-2 align-items-center">
              {errorLoading && <span className="text-muted small">Loading…</span>}
              {errorRows !== null && errorRows.length > 0 && (
                <button
                  className="btn btn-warning btn-sm"
                  disabled={selectedErrors.size === 0 || retrying || errorLoading}
                  onClick={handleRetry}
                >
                  {retrying ? "Retrying…" : `Retry Selected${selectedErrors.size > 0 ? ` (${selectedErrors.size})` : ""}`}
                </button>
              )}
            </div>
          </div>
          <div className="card-body p-0">
            {retryResult && (
              <div className={`px-3 pt-2 small ${retryResult.ok ? "text-success" : "text-danger"}`}>
                {retryResult.message}
              </div>
            )}
            {(errorRows === null || errorLoading) && !errorError && (
              <div className="p-3 text-muted small">Loading…</div>
            )}
            {errorError && (
              <div className="p-3 text-danger small">{errorError}</div>
            )}
            {errorRows !== null && !errorLoading && errorRows.length === 0 && !errorError && (
              <div className="p-3 text-success small">No errors found.</div>
            )}
            {errorRows !== null && errorRows.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "32px" }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedErrors.size === errorRows.length}
                          ref={(el) => { if (el) el.indeterminate = selectedErrors.size > 0 && selectedErrors.size < errorRows.length; }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedErrors(new Set(errorRows.map((r) => `${r.layer}::${r.filepath}`)));
                            } else {
                              setSelectedErrors(new Set());
                            }
                          }}
                        />
                      </th>
                      <th style={{ width: "180px" }}>Layer</th>
                      <th>File</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorRows.map((row, i) => {
                      const key = `${row.layer}::${row.filepath}`;
                      return (
                        <tr
                          key={i}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedErrors((prev) => {
                            const next = new Set(prev);
                            next.has(key) ? next.delete(key) : next.add(key);
                            return next;
                          })}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedErrors.has(key)}
                              onChange={() => setSelectedErrors((prev) => {
                                const next = new Set(prev);
                                next.has(key) ? next.delete(key) : next.add(key);
                                return next;
                              })}
                            />
                          </td>
                          <td className="text-muted small font-monospace" style={{ whiteSpace: "nowrap" }}>{row.layer}</td>
                          <td className="font-monospace small" style={{ wordBreak: "break-all" }}>{row.filepath}</td>
                          <td className="text-danger small" style={{ wordBreak: "break-all" }}>{row.error_message || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
